import { Stack, type StackProps, type SxProps, type Theme } from '@mui/material';
import type { ButtonProps } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    andieAgentToArea,
    buildAndiePresetMessage,
    getRandomPresetsForArea,
    type AndieActionArea,
} from '@/ai/data/andie-action-presets';
import { useAndieChatControls } from '@/providers/andie-chat-provider';
import AndieButton from './AndieButton';

interface AndieActionPresetsProps {
    /** How many random presets to show. Defaults to 4. */
    count?: number;
    /** Force a specific area instead of deriving it from the active agent. */
    area?: AndieActionArea;
    /** Forwarded to each preset button. */
    variant?: ButtonProps['variant'];
    /** Extra styling for each preset button. */
    buttonSx?: SxProps<Theme>;
    /** Layout of the buttons row. Defaults to centered, wrapping. */
    justifyContent?: StackProps['justifyContent'];
    /** Runs right before a preset starts its conversation (e.g. open the chat drawer). */
    onBeforeStart?: () => void;
}

/**
 * Self-contained Andie starting-prompt buttons. Picks a random set of presets
 * filtered by the current area (insights/content/general → see
 * {@link getRandomPresetsForArea}) and, on click, opens a fresh conversation
 * seeded with the (translated) prompt plus any hidden context.
 *
 * Drop it anywhere inside the AndieChatProvider + SessionProvider tree.
 */
const AndieActionPresets = ({
    count = 4,
    area,
    variant,
    buttonSx,
    justifyContent = 'center',
    onBeforeStart,
}: AndieActionPresetsProps) => {
    const { t } = useTranslation();
    const { agentId, startNewConversation } = useAndieChatControls();

    const resolvedArea = area ?? andieAgentToArea(agentId);
    const presets = useMemo(() => getRandomPresetsForArea(resolvedArea, count), [resolvedArea, count]);

    return (
        <Stack direction="row" gap={1} flexWrap="wrap" justifyContent={justifyContent}>
            {presets.map((preset) => (
                <AndieButton
                    key={preset.key}
                    size="small"
                    variant={variant}
                    startIcon={<preset.icon />}
                    sx={buttonSx}
                    onClick={() => {
                        onBeforeStart?.();
                        startNewConversation(buildAndiePresetMessage(preset, t(preset.messageKey)));
                    }}
                >
                    {t(preset.labelKey)}
                </AndieButton>
            ))}
        </Stack>
    );
};

export default AndieActionPresets;
